import {
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
  User,
} from 'oidc-client-ts';
import { env } from 'env';

const {
  // @ts-ignore
  REACT_APP_COGNITO_CLIENT_ID,
  // @ts-ignore
  REACT_APP_COGNITO_USERPOOL_ID,
  // @ts-ignore
  REACT_APP_COGNITO_DOMAIN,
} = env;

export interface ICognitoParams {
  userPoolId: string;
  userPoolClientId: string;
  userPoolDomain: string;
}

/**
 * Cognito application baked into `env.js` at deploy time.
 *
 * Used for single-tenant deployments and as the fallback whenever a world does
 * not carry its own pool, so an unreachable settings endpoint degrades to the
 * platform pool rather than an unusable login screen.
 */
export const envCognitoParams: ICognitoParams = {
  userPoolId: REACT_APP_COGNITO_USERPOOL_ID,
  userPoolClientId: REACT_APP_COGNITO_CLIENT_ID,
  userPoolDomain: REACT_APP_COGNITO_DOMAIN,
};

/**
 * Builds the Cognito OIDC issuer URL for a user pool.
 *
 * The issuer — not the hosted-UI domain — serves
 * `/.well-known/openid-configuration`, so this is what `authority` must point
 * at for metadata discovery to resolve.
 *
 * @param userPoolId - Pool id, whose prefix up to the first `_` is its region.
 * @returns The issuer URL, or an empty string when the pool id is unusable.
 */
export const buildCognitoAuthority = (userPoolId: string): string => {
  const region = userPoolId?.split('_')[0];

  if (!region || !userPoolId.includes('_')) {
    return '';
  }

  return `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`;
};

/**
 * Builds the `oidc-client-ts` settings for a Cognito application.
 *
 * Mirrors the Amplify config this replaced: authorization-code flow, the
 * `openid` scope, and the app origin as both redirect targets.
 *
 * The hosted-UI endpoints (`/oauth2/authorize`, `/oauth2/token`, `/logout`)
 * are not built from `userPoolDomain`: the pool's discovery document already
 * carries all three, pointing at the pool's own domain. Seeding them here
 * would override correct values with a guess whenever the two disagree.
 *
 * @param params - Pool to authenticate against; defaults to the `env.js` pool.
 * @returns Settings ready to hand to a `UserManager`.
 */
export const buildOidcSettings = ({
  userPoolId,
  userPoolClientId,
}: ICognitoParams = envCognitoParams): UserManagerSettings => ({
  authority: buildCognitoAuthority(userPoolId),
  client_id: userPoolClientId,
  redirect_uri: window.location.origin,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  scope: 'openid',
  // Amplify persisted its session in localStorage, so a participant with an
  // open tab keeps their session across this migration and through a reload.
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Renew from the refresh token in the background rather than via a hidden
  // iframe: Cognito does not set a third-party-cookie-safe session cookie, so
  // silent iframe renew fails in browsers that block them.
  automaticSilentRenew: true,
  monitorSession: false,
});

/**
 * Creates the `UserManager` for a Cognito application.
 *
 * @param params - Pool to authenticate against; defaults to the `env.js` pool.
 * @returns A `UserManager` bound to that pool.
 */
export const createUserManager = (
  params: ICognitoParams = envCognitoParams,
): UserManager => new UserManager(buildOidcSettings(params));

/**
 * The `UserManager` backing the token helpers below.
 *
 * Set once by `AuthProvider` as soon as the tenant pool resolves. It exists so
 * non-React callers — the axios interceptor, the websocket URL builder, the
 * assistant service token providers — can read the live session without
 * threading a hook through them.
 */
let activeUserManager: UserManager | null = null;

/**
 * Records the `UserManager` the token helpers should read from.
 *
 * @param userManager - Manager for the resolved tenant pool, or `null` on teardown.
 */
export const setActiveUserManager = (userManager: UserManager | null) => {
  activeUserManager = userManager;
};

/**
 * Whether the OIDC client has been configured yet.
 *
 * @returns `true` once `AuthProvider` has resolved the pool.
 */
export const isAuthConfigured = (): boolean => activeUserManager !== null;

/**
 * Loads the stored OIDC user, refreshing it when the access token has expired.
 *
 * Amplify's `fetchAuthSession` refreshed implicitly; here the refresh is
 * explicit, so every caller that relied on that behavior — notably the
 * websocket URL builder, which must never replay a dead token — keeps getting
 * a live token.
 *
 * @param options.forceRefresh - Refresh even when the stored token is still valid.
 * @returns The current user, or `null` when there is no usable session.
 */
export const getOidcUser = async ({
  forceRefresh = false,
}: { forceRefresh?: boolean } = {}): Promise<User | null> => {
  if (!activeUserManager) {
    return null;
  }

  try {
    const user = await activeUserManager.getUser();

    if (!user) {
      return null;
    }

    if (!forceRefresh && !user.expired) {
      return user;
    }

    // No refresh token (e.g. the client has no refresh grant) — the stored
    // token is all there is, so hand it back rather than dropping the session.
    if (!user.refresh_token) {
      return user;
    }

    return await activeUserManager.signinSilent();
  } catch (error) {
    console.error(`Failed to load OIDC session: ${(error as Error).message}`, {
      userPoolClientId: activeUserManager.settings.client_id,
    });

    return null;
  }
};

/**
 * Returns a live access token, refreshing the session when needed.
 *
 * @param options.forceRefresh - Refresh even when the stored token is still valid.
 * @returns The access token, or `null` when there is no usable session.
 */
export const getAccessToken = async (options?: {
  forceRefresh?: boolean;
}): Promise<string | null> => {
  const user = await getOidcUser(options);

  return user?.access_token ?? null;
};

/**
 * Whether this page load is the return leg of a hosted-UI sign-in.
 *
 * Replaces the `Hub.listen('auth')` / `signInWithRedirect` event the Amplify
 * version used to decide whether to emit the login analytics event. Set while
 * the redirect is being processed and read once the websocket session and the
 * user profile are both up, so it must survive that gap rather than being a
 * transient callback argument.
 */
let signedInViaRedirect = false;

/**
 * Whether the participant arrived from a completed hosted-UI sign-in.
 *
 * @returns `true` when this page load handled a sign-in redirect.
 */
export const didSignInViaRedirect = (): boolean => signedInViaRedirect;

/**
 * Strips the `?code=&state=` handoff from the URL after a successful sign-in,
 * and records that this load came from a sign-in redirect.
 *
 * Passed to react-oidc-context's provider (it is a provider prop, not a
 * `UserManager` setting), so the redirect params stay out of the participant's
 * history and out of anything that later logs the URL.
 */
export const onSigninCallback = () => {
  signedInViaRedirect = true;

  window.history.replaceState({}, document.title, window.location.pathname);
};

/**
 * Whether the current URL still carries an unprocessed sign-in response.
 *
 * Cognito returns the authorization code (or an error) in the query string, so
 * their presence means a redirect is mid-flight and a session may be moments
 * away.
 *
 * @returns `true` while `?code=` or `?error=` is still on the URL.
 */
const hasPendingSigninResponse = (): boolean => {
  const params = new URLSearchParams(window.location.search);

  return params.has('code') || params.has('error');
};

/**
 * Loads the session, briefly retrying while it is still absent.
 *
 * Replaces the flat 1s sleep the Amplify implementation used to outwait the
 * post-redirect code exchange: `AuthProvider` already awaits that exchange, so
 * the common case returns on the first attempt with no delay. The retry only
 * costs anything on the paths that genuinely have no session yet, and it is
 * bounded so an unauthenticated visitor still reaches the login screen
 * promptly.
 *
 * @param options.forceRefresh - Refresh even when the stored token is still valid.
 * @returns The current user, or `null` if none appeared within the budget.
 */
export const waitForSession = async (options?: {
  forceRefresh?: boolean;
}): Promise<User | null> => {
  const RETRY_DELAY_MS = 100;
  const MAX_ATTEMPTS = 10;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    const user = await getOidcUser(options);

    if (user) {
      return user;
    }

    // Nothing to wait for unless a redirect is mid-flight.
    if (!hasPendingSigninResponse()) {
      return null;
    }

    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
  }

  return null;
};

/** Session shape the shared `useAuthTokens` hook consumes. */
export interface IAuthTokens {
  accessToken: {
    toString: () => string;
    payload: { exp?: number };
  };
}

/**
 * Adapts an OIDC user to the Amplify-shaped session `useAuthTokens` expects.
 *
 * @param user - OIDC user, or `null` when there is no session.
 * @returns The adapted session, or `null` when there is no access token.
 */
export const toAuthTokens = (user: User | null): IAuthTokens | null => {
  if (!user?.access_token) {
    return null;
  }

  return {
    accessToken: {
      toString: () => user.access_token,
      // `expires_at` is epoch seconds, matching the JWT `exp` claim the
      // refresh scheduler reads.
      payload: { exp: user.expires_at },
    },
  };
};

/**
 * The current user's Cognito identity, in the shape the provider consumed from
 * Amplify's `getCurrentUser()`.
 */
export interface IAuthUser {
  username: string;
  signInDetails?: { loginId?: string };
}

/**
 * Reads the signed-in participant's Cognito identity from the ID token.
 *
 * Replaces Amplify's `getCurrentUser()`. `username` keeps mapping to the
 * Cognito `sub`, which is what the previous implementation returned and what
 * Sentry is keyed on.
 *
 * `loginId` is best-effort: Cognito only puts an `email` claim in the ID token
 * when the `email` scope is granted, and this client requests `openid` alone
 * (matching the Amplify config, which sourced the address from the sign-in
 * flow rather than the token). Its only consumer is the non-prod Sentry
 * identity, which tolerates an empty string — so an absent claim degrades that
 * one field instead of failing the login. Add `email` to `scope` in
 * `buildOidcSettings` (and to the Cognito app client's allowed scopes) if it
 * ever needs to be reliable.
 *
 * @returns The identity, or `null` when there is no session.
 */
export const getCurrentUser = async (): Promise<IAuthUser | null> => {
  const user = await getOidcUser();

  if (!user) {
    return null;
  }

  const profile = user.profile ?? {};

  return {
    username: (profile.sub as string) ?? '',
    signInDetails: {
      loginId:
        (profile.email as string) ??
        (profile['cognito:username'] as string) ??
        '',
    },
  };
};

/**
 * Redirects to the Cognito hosted UI to sign in.
 *
 * @param params.lang - Hosted-UI locale, appended as `lang` to /oauth2/authorize.
 */
export const signInWithRedirect = async ({
  lang,
}: { lang?: string } = {}): Promise<void> => {
  if (!activeUserManager) {
    console.error('Sign-in requested before the OIDC client was configured', {
      userPoolClientId: envCognitoParams.userPoolClientId,
    });

    return;
  }

  await activeUserManager.signinRedirect(
    lang ? { extraQueryParams: { lang } } : undefined,
  );
};

/**
 * Clears the local session and redirects to the Cognito hosted UI /logout.
 *
 * The local store is cleared first so a hosted-UI redirect that never returns
 * (network failure, blocked navigation) still leaves the tab signed out.
 */
export const signOut = async (): Promise<void> => {
  if (!activeUserManager) {
    return;
  }

  const userManager = activeUserManager;

  try {
    await userManager.removeUser();

    await userManager.signoutRedirect({
      extraQueryParams: {
        client_id: userManager.settings.client_id,
        logout_uri: window.location.origin,
      },
    });
  } catch (error) {
    console.error(`Failed to sign out: ${(error as Error).message}`, {
      userPoolClientId: userManager.settings.client_id,
    });

    // The hosted-UI redirect failed but the local session is already gone —
    // land the participant on the login screen rather than a half-signed-out app.
    window.location.assign(window.location.origin);
  }
};
